import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";

const EVOLUTION_IMG = "https://images.unsplash.com/photo-1768005867860-7371c47e660d?auto=format&fit=crop&w=1400&q=80";

const eras = [
  { year: "1995", title: "The Listing Era", body: "Match.com launches. For the first time, people could search for a partner online. Profiles were static. Discovery was manual." },
  { year: "2004", title: "The Algorithm Era", body: "OkCupid introduces compatibility scoring. eHarmony builds on questionnaires. The promise: let data do the work. The reality: more data, same problems." },
  { year: "2012", title: "The Swipe Era", body: "Tinder changes everything. Speed replaces depth. Volume replaces intention. Dating becomes a game — and engagement becomes the product." },
  { year: "2016", title: "The Niche Era", body: "Hinge, Bumble, and others try to fix swiping with rules. Ladies go first. Prompts replace bios. But the core model stays the same: browse, choose, hope." },
  { year: "2020", title: "The Fatigue Era", body: "Burnout sets in. Users report spending hours per week with diminishing results. The platforms optimize for retention, not resolution." },
  { year: "Now", title: "The Network Era", body: "HAEVN flips the model. You don't browse. You don't swipe. You define what you want once, and we surface the people who actually align — starting at 80% compatibility.", highlight: true },
];

export default function EvolutionPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="evolution-page">
      <SEO
        title="Evolution of Digital Dating"
        path="/evolution"
        description="From Match.com to Tinder to HAEVN — trace the evolution of digital dating from listings to swiping to intentional matching."
      />
      <Navigation />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={EVOLUTION_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/50" />
        </div>
        <div className="relative z-10 pt-20 pb-16 md:pt-28 md:pb-20 px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeIn>
              <h1 className="font-headline text-4xl sm:text-5xl text-haevn-navy mb-4">
                The evolution of digital dating
              </h1>
            </FadeIn>
            <FadeIn delay={100}>
              <p className="text-base md:text-lg text-gray-500 font-body">
                How we got here — and why it was time for something different.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

      {/* Timeline */}
      <section className="py-16 px-6 md:px-12 bg-[#F8F7F5]">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-gray-300" />
            <div className="space-y-14">
              {eras.map((era, i) => (
                <FadeIn key={era.year} delay={i * 80}>
                  <div className="relative pl-14 md:pl-16">
                    <div className={`absolute left-2 md:left-4 top-1 w-5 h-5 rounded-full border-2 ${
                      era.highlight ? "bg-haevn-teal border-haevn-teal" : "bg-white border-gray-300"
                    }`} />
                    <span className={`text-xs font-subhead uppercase tracking-widest mb-1 block ${
                      era.highlight ? "text-haevn-teal" : "text-gray-400"
                    }`}>{era.year}</span>
                    <h3 className={`font-headline text-xl mb-2 ${era.highlight ? "text-haevn-navy" : "text-gray-800"}`}>
                      {era.title}
                    </h3>
                    <p className={`text-base font-body leading-relaxed ${era.highlight ? "text-gray-700" : "text-gray-500"}`}>
                      {era.body}
                    </p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-haevn-navy">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 bg-haevn-gold text-white px-10 py-4 font-subhead text-lg hover:bg-haevn-gold/90 transition-all duration-200 group"
              data-testid="evolution-cta-btn"
            >
              See how HAEVN works
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
